import type { NodeArtifact, NodeOutput } from './node-output';
import { aiMimeTypes } from '../constants';
import type { FenceMarker } from '../serializers/markdown/code-fence';
import {
  getIsFenceCloser,
  getIsMachineOutputFence,
  parseFenceMarker,
} from '../serializers/markdown/code-fence';

/**
 * Whether any plan node produced any streamed content — prose, tool-call
 * fences, or reasoning.
 *
 * The harness renders the assistant message purely from each node's
 * `displayText` (the AgentMessage `text` column is intentionally unused).
 * This "did anything stream at all?" signal is what the client uses to
 * tear down the thinking spinner, where a tool-call fence legitimately
 * counts as visible work.
 */
export function hasVisibleNodeOutput(nodeOutputs: Record<string, NodeOutput>): boolean {
  return Object.values(nodeOutputs).some(output => output.displayText.trim().length > 0);
}

/**
 * Whether a single node produced something the user actually sees: prose, or
 * a user-facing artifact (a card). Computed at persist time from the runner's
 * returned prose and artifacts, then stored as the `hasVisibleAnswer` boolean.
 *
 * The prose text passed here is the runner's *returned* text, which is prose
 * alone for the prose agents (repository/summarizer/analyzer return
 * `processFullAIStream().text` — empty in the AI-47 tool-only case). The
 * editor/drafter return fenced text instead, but they always emit a
 * user-facing artifact, so the artifact term — not the fenced prose — is what
 * makes their answer count. That is why the fence content never has to be
 * separated out for those agents.
 */
export function getHasVisibleAnswer(args: {
  proseText: string;
  artifacts?: NodeArtifact[];
}): boolean {
  return (
    args.proseText.trim().length > 0 || getHasUserFacingArtifact({ artifacts: args.artifacts })
  );
}

/**
 * Whether the turn produced a user-facing answer — the finalize tripwire's
 * "did the user get anything?" signal, distinct from `hasVisibleNodeOutput`
 * (spinner teardown). Reads the persisted `hasVisibleAnswer` boolean only; it
 * never parses `displayText`, so fence-laden tool-call output can't fool it.
 *
 * Catches the AI-47 failure mode: a turn that streams only tool calls plus a
 * `documentSelection` handoff renders a blank message, and every such node
 * persists `hasVisibleAnswer: false`, so the turn reports no answer.
 *
 * A node whose `hasVisibleAnswer` is `undefined` is indeterminate — a row
 * persisted before this field existed, or a mid-stream `streaming` snapshot
 * whose terminal write was lost. We refuse to call the turn blank in that
 * case, so a deploy in the middle of live turns (or a leaked snapshot) can't
 * discredit the signal with false alarms. Trade-off: a turn that mixes one
 * confirmed-blank node with a legacy/indeterminate node won't fire — a narrow
 * deploy-window gap we accept to keep the tripwire precise.
 */
export function hasUserFacingAnswer(nodeOutputs: Record<string, NodeOutput>): boolean {
  const outputs = Object.values(nodeOutputs);
  if (outputs.length === 0) return false;
  if (outputs.some(output => output.hasVisibleAnswer === undefined)) return true;
  return outputs.some(output => output.hasVisibleAnswer === true);
}

/**
 * The text of a `displayText` that the user can actually read, with
 * machine-output fences removed.
 *
 * Reasoning, tool calls and artifact payloads render as collapsed cards rather
 * than as an answer, so they do not count as the assistant addressing the user.
 * An ordinary code block does count, because a turn whose answer is a code
 * sample has answered.
 *
 * Fence recognition is shared with the client's `remarkCodeBlock` plugin via
 * `code-fence.ts`, so this cannot drift from what the renderer shows. An
 * unclosed machine-output fence therefore swallows the remainder, exactly as
 * the renderer does.
 */
export function getUserVisibleText(displayText: string): string {
  const visibleLines: string[] = [];
  let opener: FenceMarker | null = null;

  for (const line of displayText.split('\n')) {
    const marker = parseFenceMarker(line);

    if (opener === null) {
      // Outside any block: a marker opens one, everything else is readable.
      if (marker) opener = marker;
      else visibleLines.push(line);
      continue;
    }

    // Inside a block: only a matching closer ends it, per CommonMark.
    if (marker && getIsFenceCloser(marker, opener)) {
      opener = null;
      continue;
    }
    if (getIsMachineOutputFence(opener.language)) continue;
    visibleLines.push(line);
  }

  return visibleLines.join('\n').trim();
}

/**
 * Whether the turn left the user something to read, rather than only cards.
 *
 * Distinct from `hasUserFacingAnswer`, which a single artifact satisfies: an
 * editor node emits a document card and fenced tool output, so it counts as
 * having answered without ever addressing the user. A turn whose terminal node
 * is that editor renders a thought process, some cards, and nothing saying what
 * happened, which reads as still working.
 *
 * Parses `displayText` rather than reading a persisted flag, because the flag
 * that would carry this (`hasVisibleAnswer`) deliberately folds text and
 * artifacts together and cannot tell them apart after the fact.
 *
 * A `redline-note` inside a `jsonl-redline-edit` fence is also user-facing:
 * the client renders it as a readable card (for example, asking the user to
 * attach a document). Counting only unfenced prose would treat that turn as
 * blank and invite a false "edits are ready" repair.
 */
export function hasUserFacingText(nodeOutputs: Record<string, NodeOutput>): boolean {
  return Object.values(nodeOutputs).some(
    output =>
      getUserVisibleText(output.displayText).length > 0 ||
      hasReadableRedlineNote(output.displayText)
  );
}

/**
 * Whether `displayText` contains at least one editor note the client would
 * render. Notes live inside machine-output fences, so `getUserVisibleText`
 * strips them; this is the check that still sees them.
 */
export function hasReadableRedlineNote(displayText: string): boolean {
  for (const body of collectFenceBodies(displayText, aiMimeTypes.jsonlRedlineEdit)) {
    for (const line of body.split('\n')) {
      if (getIsReadableRedlineNoteLine(line)) return true;
    }
  }
  return false;
}

/**
 * Whether any node is still mid-flight or carries an unknowable answer signal:
 * a `streaming` snapshot, a row persisted before `hasVisibleAnswer` existed, or
 * one whose terminal write was lost.
 *
 * `hasUserFacingAnswer` deliberately reports `true` for a missing flag so a
 * deploy landing mid-turn cannot raise a false blank-turn alarm. That leniency
 * would otherwise let the text backstop fire on a partial stream that simply
 * had not reached its text yet. Status is checked as well as the flag, so the
 * guard does not depend on streaming snapshots continuing to omit it.
 */
export function hasUnsettledNodeOutput(nodeOutputs: Record<string, NodeOutput>): boolean {
  return Object.values(nodeOutputs).some(
    output => output.hasVisibleAnswer === undefined || output.status === 'streaming'
  );
}

/**
 * Artifact kinds that are internal handoffs to a downstream node and render
 * nothing to the user on their own. `documentSelection` hands selected
 * document IDs to a later drafting/answering stage; every other artifact
 * kind (`draftDocument`, `editedDocument`, `createdReport`) renders a card
 * the user sees. A denylist — not an allowlist — so a newly added artifact
 * kind defaults to "user-facing" and can't silently start tripping the
 * empty-output backstop.
 */
const NON_VISIBLE_ARTIFACT_KINDS = new Set<NodeArtifact['kind']>(['documentSelection']);

function getHasUserFacingArtifact(args: { artifacts?: NodeArtifact[] }): boolean {
  return Boolean(args.artifacts?.some(getIsUserFacingArtifact));
}

/**
 * A document artifact renders one card per document, so an empty `documents`
 * array renders nothing and must not count as the user having been answered.
 * The persistence helpers return `null` rather than build one, but the schema
 * still permits it, and letting an empty artifact satisfy the answer invariant
 * would leave a turn finalizing with nothing on screen and no warning.
 */
function getIsUserFacingArtifact(artifact: NodeArtifact): boolean {
  if (NON_VISIBLE_ARTIFACT_KINDS.has(artifact.kind)) return false;
  if (artifact.kind === 'draftDocument' || artifact.kind === 'editedDocument')
    return artifact.documents.length > 0;
  return true;
}

/**
 * Bodies of fenced blocks whose opener language matches `language`, including
 * an unclosed trailing fence (same swallow rule as the renderer).
 */
function collectFenceBodies(displayText: string, language: string): string[] {
  const bodies: string[] = [];
  let opener: FenceMarker | null = null;
  let bodyLines: string[] = [];

  for (const line of displayText.split('\n')) {
    const marker = parseFenceMarker(line);
    if (opener === null) {
      if (marker && marker.language === language) {
        opener = marker;
        bodyLines = [];
      }
    } else if (marker && getIsFenceCloser(marker, opener)) {
      bodies.push(bodyLines.join('\n'));
      opener = null;
    } else {
      bodyLines.push(line);
    }
  }

  if (opener !== null) bodies.push(bodyLines.join('\n'));
  return bodies;
}

function getIsReadableRedlineNoteLine(line: string): boolean {
  const trimmed = line.trim();
  if (trimmed.length === 0) return false;
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return false;
  }
  if (typeof parsed !== 'object' || parsed === null) return false;
  if (!('type' in parsed) || parsed.type !== 'redline-note') return false;
  if (!('text' in parsed) || typeof parsed.text !== 'string') return false;
  return parsed.text.trim().length > 0;
}
